import React, { useState, useRef, useCallback } from 'react';

const ACCEPTED_TYPES = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
const MAX_SIZE = 10 * 1024 * 1024;

async function compressImage(file) {
  if (!file.type.startsWith('image/')) return file;
  if (file.size < 500000) return file;

  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const maxWidth = 1600;
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        canvas.toBlob(
          (blob) => {
            if (blob) {
              resolve(new File([blob], file.name, { type: file.type, lastModified: Date.now() }));
            } else {
              resolve(file);
            }
          },
          file.type,
          0.85
        );
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(file);
  });
}

const FileUpload = ({ label, docType, files, onFilesChange, multiple = true, required = false }) => {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const processFiles = useCallback(async (fileList) => {
    const newFiles = [];
    for (const file of Array.from(fileList)) {
      if (!ACCEPTED_TYPES.includes(file.type) && !file.name.match(/\.(pdf|jpe?g|png)$/i)) {
        alert(`${file.name}: Only PDF, JPG, JPEG, PNG allowed.`);
        continue;
      }
      if (file.size > MAX_SIZE) {
        alert(`${file.name}: File exceeds 10 MB limit.`);
        continue;
      }
      const processed = await compressImage(file);
      const preview = processed.type.startsWith('image/')
        ? URL.createObjectURL(processed)
        : null;
      newFiles.push({
        file: processed,
        docType,
        preview,
        name: processed.name,
        size: processed.size,
      });
    }
    if (newFiles.length) {
      if (multiple) {
        onFilesChange([...files, ...newFiles]);
      } else {
        // Replace only this document type; keep uploads for other types
        const otherFiles = files.filter((f) => f.docType !== docType);
        onFilesChange([...otherFiles, ...newFiles]);
      }
    }
  }, [docType, files, multiple, onFilesChange]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    processFiles(e.dataTransfer.files);
  };

  const removeFile = (index) => {
    const updated = files.filter((_, i) => i !== index);
    onFilesChange(updated);
  };

  const sectionFiles = files.filter((f) => f.docType === docType);

  return (
    <div className="file-upload-section">
      <label className="file-upload-label">
        {label} {required && '*'}
      </label>
      <div
        className={`file-drop-zone ${dragOver ? 'drag-over' : ''}`}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
      >
        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png"
          multiple={multiple}
          style={{ display: 'none' }}
          onChange={(e) => {
            processFiles(e.target.files);
            e.target.value = '';
          }}
        />
        <div className="drop-zone-content">
          <span className="drop-icon">📁</span>
          <p>Drag & drop files here or click to browse</p>
          <small>PDF, JPG, JPEG, PNG — Max 10 MB (images auto-compressed)</small>
        </div>
      </div>

      {sectionFiles.length > 0 && (
        <div className="file-preview-list">
          {sectionFiles.map((item, idx) => {
            const globalIdx = files.indexOf(item);
            return (
              <div key={`${item.name}-${idx}`} className="file-preview-item">
                {item.preview ? (
                  <img src={item.preview} alt={item.name} className="file-thumb" />
                ) : (
                  <div className="file-thumb pdf-thumb">PDF</div>
                )}
                <div className="file-info">
                  <span className="file-name">{item.name}</span>
                  <span className="file-size">{(item.size / 1024).toFixed(1)} KB</span>
                </div>
                <button type="button" className="file-remove-btn" onClick={() => removeFile(globalIdx)}>
                  ✕
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default FileUpload;
